import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
// Required for FullCalendar to honor a named IANA timeZone ("Asia/Dubai").
// Without it, timeZone falls back to UTC and every event renders ~4h off.
import luxonPlugin from '@fullcalendar/luxon3';
import type { EventInput, EventClickArg } from '@fullcalendar/core';
import { BRAND } from '@fin/shared/brand';
import { SERVICE_LABELS, STATUS_COLORS, STATUS_LABELS, type JobStatus } from '@fin/shared/enums';
import { useAuth } from '../lib/auth';
import { useI18n, LocaleToggle } from '../lib/i18n';
import { useMyJobs, type Job } from '../lib/queries';
import { NotificationsBell } from '../components/NotificationsBell';

export function Today() {
  const { user, signOut } = useAuth();
  const { t, locale } = useI18n();
  const jobs = useMyJobs();
  const [view, setView] = useState<'day' | 'calendar'>('calendar');

  const today = new Date();
  // Day comparisons must use Asia/Dubai (the operating timezone). Using
  // browser-local time caused jobs scheduled "today in Dubai" to disappear
  // from the Today list whenever the browser was in a different TZ
  // (e.g. UTC) and had crossed midnight.
  const dubaiDayKey = (iso: string | Date) =>
    new Date(typeof iso === 'string' ? iso : iso.toISOString())
      .toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai' }); // YYYY-MM-DD
  const todayKey = dubaiDayKey(today);
  const isSameDay = (iso: string) => dubaiDayKey(iso) === todayKey;

  const TERMINAL: JobStatus[] = ['completed', 'cancelled'];
  const isTerminal = (s: string) => TERMINAL.includes(s as JobStatus);

  const all = jobs.data ?? [];
  const todaysAll = all.filter((j) => isSameDay(j.scheduledAt));
  const todaysActive = todaysAll
    .filter((j) => !isTerminal(j.status))
    .sort((a, b) => +new Date(a.scheduledAt) - +new Date(b.scheduledAt));
  const todaysDone = todaysAll
    .filter((j) => isTerminal(j.status))
    .sort((a, b) => +new Date(b.scheduledAt) - +new Date(a.scheduledAt));
  const upcoming = all
    .filter(
      (j) =>
        dubaiDayKey(j.scheduledAt) > todayKey &&
        !isTerminal(j.status),
    )
    .sort((a, b) => +new Date(a.scheduledAt) - +new Date(b.scheduledAt));

  return (
    <main className="min-h-screen pb-12" style={{ backgroundColor: BRAND.surfaceMuted }}>
      <header
        className="sticky top-0 z-20 flex items-center justify-between gap-2 px-3 py-3 text-white shadow-lg sm:px-4"
        style={{
          backgroundImage: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primarySoft} 100%)`,
        }}
      >
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          <img
            src="/logo.png"
            alt="FixItNinjaz"
            className="h-7 w-auto shrink-0 sm:h-8"
            style={{ filter: 'brightness(0) invert(1)' }}
          />
          <div className="hidden min-w-0 leading-tight sm:block">
            <p className="text-[10px] uppercase tracking-wider opacity-70">{t('app.signed_in_as')}</p>
            <p className="truncate text-xs font-semibold">{user?.email}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <LocaleToggle />
          <RefreshButton />
          <div className="text-white">
            <NotificationsBell />
          </div>
          <button
            onClick={() => void signOut()}
            title={t('app.sign_out')}
            aria-label={t('app.sign_out')}
            className="flex h-9 items-center justify-center rounded-md border border-white/20 bg-white/5 px-2 backdrop-blur transition hover:bg-white/15 sm:px-3"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-4 w-4 sm:hidden"
              aria-hidden
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            <span className="hidden text-xs sm:inline">{t('app.sign_out')}</span>
          </button>
        </div>
      </header>

      <section className="px-3 py-4 sm:px-4">
        <div
          className="relative mb-4 overflow-hidden rounded-2xl px-5 py-4 shadow-sm sm:px-6 sm:py-5"
          style={{
            backgroundImage: `linear-gradient(135deg, #92400E 0%, #FBBF24 100%)`,
          }}
        >
          <div
            aria-hidden
            className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full opacity-20 blur-2xl"
            style={{ background: 'white' }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-10 -left-6 h-28 w-28 rounded-full opacity-10 blur-2xl"
            style={{ background: 'white' }}
          />
          <div className="relative flex items-center gap-3">
            <div
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white shadow-sm"
              style={{ background: 'rgba(255,255,255,0.2)' }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-semibold text-white drop-shadow-sm sm:text-2xl">
                {t('today.title')}
              </h1>
              <p className="text-xs text-white/85 sm:text-sm">
                {today.toLocaleDateString(locale === 'ar' ? 'ar-AE' : 'en-AE', { timeZone: 'Asia/Dubai', dateStyle: 'full' })}
              </p>
            </div>
          </div>
        </div>

        {jobs.isPending && <p className="mt-6 text-sm text-stone-500">{t('today.loading')}</p>}
        {jobs.isError && (
          <p className="mt-6 text-sm" style={{ color: BRAND.danger }}>
            {t('today.failed')} {(jobs.error as Error).message}
          </p>
        )}

        {jobs.data && (
          <>
            {/* Day / Week tab switcher */}
            <div
              role="tablist"
              aria-label="View switcher"
              className="mb-3 inline-flex rounded-full border bg-white p-0.5 shadow-sm"
              style={{ borderColor: BRAND.border }}
            >
              {(['day', 'calendar'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  role="tab"
                  aria-selected={view === v}
                  onClick={() => setView(v)}
                  className="rounded-full px-4 py-1.5 text-xs font-semibold transition"
                  style={{
                    backgroundColor: view === v ? BRAND.primary : 'transparent',
                    color: view === v ? 'white' : BRAND.text,
                  }}
                >
                  {t(`today.tab.${v}` as 'today.tab.day' | 'today.tab.calendar')}
                </button>
              ))}
            </div>

            {view === 'calendar' ? (
              <CalendarView jobs={all} />
            ) : (
              <>
            {/* Stat chips */}
            <div className="mb-4 grid grid-cols-3 gap-2">
              <StatChip
                label={t('today.stat_active')}
                value={todaysActive.length}
                accent="#F59E0B"
              />
              <StatChip
                label={t('today.stat_done')}
                value={todaysDone.length}
                accent="#16A34A"
              />
              <StatChip
                label={t('today.stat_upcoming')}
                value={upcoming.length}
                accent={BRAND.primary}
              />
            </div>

            {/* Today - active */}
            {todaysActive.length > 0 ? (
              <SectionHeader label={t('today.active')} count={todaysActive.length} dot="#F59E0B" />
            ) : null}
            <div className="space-y-3">
              {todaysActive.map((j) => <JobCard key={j.id} job={j} />)}
            </div>

            {/* Today - done */}
            {todaysDone.length > 0 && (
              <>
                <SectionHeader label={t('today.done')} count={todaysDone.length} dot="#16A34A" />
                <div className="space-y-3 opacity-90">
                  {todaysDone.map((j) => <JobCard key={j.id} job={j} muted />)}
                </div>
              </>
            )}

            {/* Today - empty state */}
            {todaysAll.length === 0 && (
              <div
                className="rounded-xl border bg-white p-6 text-center text-sm text-stone-500"
                style={{ borderColor: BRAND.border }}
              >
                {t('today.empty')}
              </div>
            )}

            {/* Today - all done celebratory state */}
            {todaysAll.length > 0 && todaysActive.length === 0 && (
              <div
                className="mt-3 rounded-xl border bg-white p-4 text-center text-sm font-medium"
                style={{ borderColor: '#BBF7D0', color: '#166534', backgroundColor: '#F0FDF4' }}
              >
                🎉 {t('today.all_done')}
              </div>
            )}

            {/* Upcoming */}
            {upcoming.length > 0 && (
              <>
                <SectionHeader
                  label={t('today.upcoming')}
                  count={upcoming.length}
                  dot={BRAND.primary}
                />
                <div className="space-y-3">
                  {upcoming.slice(0, 5).map((j) => <JobCard key={j.id} job={j} />)}
                </div>
              </>
            )}
              </>
            )}
          </>
        )}
      </section>
    </main>
  );
}

function CalendarView({ jobs }: { jobs: Job[] }) {
  const navigate = useNavigate();
  const { locale } = useI18n();
  const lang = locale === 'ar' ? 'ar-AE' : 'en-AE';
  // Detect small screens once on mount; FullCalendar's default toolbar buttons
  // overflow on phones, so we swap to a list view + compact toolbar.
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches;

  const events: EventInput[] = jobs.map((j) => {
    const start = new Date(j.scheduledAt);
    const end = new Date(start.getTime() + (j.estimatedDurationMinutes || 60) * 60_000);
    const serviceLabel =
      SERVICE_LABELS[j.serviceType as keyof typeof SERVICE_LABELS]?.[locale] ?? j.serviceType;
    return {
      id: j.id,
      // Star the worker's own jobs so they stand out among the whole team's.
      title: j.mine ? `★ ${serviceLabel}` : serviceLabel,
      // Anytime jobs have no committed slot — render them as all-day so they
      // don't imply a specific hour.
      ...(j.anytime
        ? { start: start.toISOString().slice(0, 10), allDay: true }
        : { start: start.toISOString(), end: end.toISOString() }),
      backgroundColor: STATUS_COLORS[j.status],
      borderColor: STATUS_COLORS[j.status],
      textColor: 'white',
      extendedProps: { status: j.status, mine: !!j.mine },
    };
  });
  function handleClick(arg: EventClickArg) {
    navigate(`/jobs/${arg.event.id}`);
  }
  return (
    <div
      className="worker-cal rounded-xl border bg-white p-2 shadow-sm sm:p-3"
      style={{ borderColor: BRAND.border }}
    >
      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin, luxonPlugin]}
        timeZone="Asia/Dubai"
        initialView={isMobile ? 'listWeek' : 'dayGridMonth'}
        headerToolbar={
          isMobile
            ? { left: 'prev,next', center: 'title', right: 'today' }
            : { left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek' }
        }
        footerToolbar={
          isMobile
            ? { center: 'listWeek,dayGridMonth,timeGridDay' }
            : undefined
        }
        buttonText={{
          today: 'Today',
          month: 'Month',
          week: 'Week',
          day: 'Day',
          list: 'List',
        }}
        events={events}
        editable={false}
        allDaySlot
        nowIndicator
        slotMinTime="06:00:00"
        slotMaxTime="22:00:00"
        firstDay={1}
        height="auto"
        contentHeight={isMobile ? 'auto' : undefined}
        dayMaxEventRows={isMobile ? 2 : 4}
        eventDisplay="block"
        eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: true }}
        eventClick={handleClick}
        locale={lang}
        noEventsText={locale === 'ar' ? 'لا توجد مهام' : 'No jobs scheduled'}
        eventDidMount={(info) => {
          // FullCalendar's list view only paints a tiny dot for the status
          // colour. Workers expect the WHOLE row to read as the status (so
          // "assigned" feels distinctly blue). Paint a left border + status
          // pill on every list row.
          const props = info.event.extendedProps as { status?: JobStatus; mine?: boolean };
          const status = props.status;
          const color = (status && STATUS_COLORS[status]) || '#64748B';
          // Dim other workers' jobs so the worker's own assignments pop.
          if (!props.mine) info.el.style.opacity = '0.55';
          if (info.view.type.startsWith('list')) {
            const row = info.el as HTMLTableRowElement;
            row.style.cursor = 'pointer';
            const firstCell = row.querySelector('td');
            if (firstCell) {
              (firstCell as HTMLElement).style.borderInlineStart = `4px solid ${color}`;
            }
            const titleCell = row.querySelector('.fc-list-event-title');
            if (titleCell && status && !titleCell.querySelector('[data-status-pill]')) {
              const pill = document.createElement('span');
              pill.dataset.statusPill = '1';
              pill.textContent = STATUS_LABELS[status][locale];
              pill.style.cssText = `display:inline-block;margin-inline-start:8px;padding:2px 8px;border-radius:9999px;font-size:10px;font-weight:600;background:${color};color:white;vertical-align:middle;`;
              titleCell.appendChild(pill);
            }
          }
        }}
      />
      <div className="mt-3 flex flex-wrap gap-1.5 text-[10px]">
        {(['assigned', 'on_my_way', 'in_progress', 'on_hold', 'completed'] as JobStatus[]).map((s) => (
          <span
            key={s}
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium"
            style={{ backgroundColor: STATUS_COLORS[s], color: 'white' }}
          >
            {STATUS_LABELS[s][locale]}
          </span>
        ))}
      </div>
    </div>
  );
}

function WeekView({ jobs }: { jobs: Job[] }) {
  const { t, locale } = useI18n();
  const lang = locale === 'ar' ? 'ar-AE' : 'en-AE';

  // Build 7 days starting today using Asia/Dubai day boundaries.
  const dayKey = (d: Date) =>
    d.toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai' });
  const today = new Date();
  const days: { date: Date; key: string; jobs: Job[] }[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(today.getTime() + i * 86_400_000);
    days.push({ date: d, key: dayKey(d), jobs: [] });
  }
  for (const j of jobs) {
    const key = dayKey(new Date(j.scheduledAt));
    const slot = days.find((d) => d.key === key);
    if (slot) slot.jobs.push(j);
  }
  for (const d of days) {
    d.jobs.sort((a, b) => +new Date(a.scheduledAt) - +new Date(b.scheduledAt));
  }

  return (
    <div className="space-y-4">
      {days.map((d, idx) => {
        const isToday = idx === 0;
        const heading = d.date.toLocaleDateString(lang, {
          timeZone: 'Asia/Dubai',
          weekday: 'long',
          day: 'numeric',
          month: 'short',
        });
        return (
          <section key={d.key}>
            <div className="mb-2 flex items-center gap-2">
              <span
                aria-hidden
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: isToday ? '#F59E0B' : BRAND.primary }}
              />
              <h2 className="text-sm font-semibold uppercase tracking-wide text-stone-700">
                {heading}
              </h2>
              <span
                className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                style={{ backgroundColor: '#F1F5F9', color: '#475569' }}
              >
                {d.jobs.length}
              </span>
            </div>
            {d.jobs.length === 0 ? (
              <p
                className="rounded-xl border bg-white px-4 py-3 text-xs text-stone-400"
                style={{ borderColor: BRAND.border }}
              >
                {t('today.week.empty_day')}
              </p>
            ) : (
              <div className="space-y-3">
                {d.jobs.map((j) => <JobCard key={j.id} job={j} />)}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

function StatChip({ label, value, accent }: { label: string; value: number; accent: string }) {
  return (
    <div
      className="rounded-xl border bg-white px-3 py-2.5 text-center shadow-sm"
      style={{ borderColor: BRAND.border }}
    >
      <p className="text-2xl font-bold leading-none" style={{ color: accent }}>
        {value}
      </p>
      <p className="mt-1 text-[10px] font-medium uppercase tracking-wide text-stone-500">
        {label}
      </p>
    </div>
  );
}

function SectionHeader({ label, count, dot }: { label: string; count: number; dot: string }) {
  return (
    <div className="mt-6 mb-2 flex items-center gap-2">
      <span
        aria-hidden
        className="inline-block h-2 w-2 rounded-full"
        style={{ backgroundColor: dot }}
      />
      <h2 className="text-sm font-semibold uppercase tracking-wide text-stone-600">
        {label}
      </h2>
      <span
        className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
        style={{ backgroundColor: '#F1F5F9', color: '#475569' }}
      >
        {count}
      </span>
    </div>
  );
}

function JobCard({ job, muted = false }: { job: Job; muted?: boolean }) {
  const { t, locale } = useI18n();
  const lang = locale === 'ar' ? 'ar-AE' : 'en-AE';
  const when = new Date(job.scheduledAt);
  const time = job.anytime
    ? t('job.anytime')
    : when.toLocaleTimeString(lang, { timeZone: 'Asia/Dubai', hour: '2-digit', minute: '2-digit' });
  const date = when.toLocaleDateString(lang, { timeZone: 'Asia/Dubai', weekday: 'short', day: 'numeric', month: 'short' });
  const statusColor = STATUS_COLORS[job.status] ?? '#64748B';
  const statusLabel = STATUS_LABELS[job.status as JobStatus]?.[locale] ?? job.status;
  return (
    <Link
      to={`/jobs/${job.id}`}
      className={`block rounded-xl border bg-white p-4 shadow-sm transition hover:shadow-md active:scale-[0.99] ${muted ? 'opacity-75' : ''} ${job.mine === false ? 'opacity-70' : ''}`}
      style={{ borderLeftColor: statusColor, borderLeftWidth: 4, borderTopColor: BRAND.border, borderRightColor: BRAND.border, borderBottomColor: BRAND.border }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-stone-500">
            {date} · {time}
            {job.mine && (
              <span
                className="ms-2 inline-block rounded-full px-1.5 py-0.5 text-[10px] font-semibold text-white"
                style={{ backgroundColor: BRAND.primary }}
              >
                {t('today.you')}
              </span>
            )}
          </p>
          <p
            className={`mt-1 text-base font-semibold sm:text-lg ${muted ? 'line-through decoration-stone-300' : ''}`}
            style={{ color: BRAND.primary }}
          >
            {SERVICE_LABELS[job.serviceType as keyof typeof SERVICE_LABELS]?.[locale] ?? job.serviceType}
          </p>
          {job.priority !== 'normal' && (
            <span
              className="mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium"
              style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}
            >
              {job.priority}
            </span>
          )}
        </div>
        <span
          className="shrink-0 rounded-full px-2.5 py-1 text-xs font-medium text-white shadow-sm"
          style={{ backgroundColor: statusColor }}
        >
          {statusLabel}
        </span>
      </div>
      {job.description && (
        <p className="mt-2 text-sm text-stone-600 line-clamp-2">{job.description}</p>
      )}
    </Link>
  );
}

function RefreshButton() {
  const qc = useQueryClient();
  const fetching = useIsFetching();
  const spinning = fetching > 0;
  return (
    <button
      type="button"
      onClick={() => void qc.invalidateQueries()}
      disabled={spinning}
      title="Refresh"
      aria-label="Refresh"
      className="rounded-md border border-white/20 bg-white/5 p-1.5 text-white backdrop-blur transition hover:bg-white/15 disabled:opacity-60"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={spinning ? 'animate-spin' : undefined}
      >
        <path d="M21 12a9 9 0 1 1-3-6.7" />
        <path d="M21 4v5h-5" />
      </svg>
    </button>
  );
}
